import styled from "@emotion/styled";
import { RemoveCircleOutline } from "@mui/icons-material"
import { Button, Typography } from "@mui/material"
import { useEffect, useRef, useState } from "react";
import { ShoppingListItem } from "./ShoppingList";


interface Props {
    items: ShoppingListItem[];
    onDelete: (id: string) => void;
    onCardReorder: (movedCardId: string, dropTo: DropResult) => void;
    height: string,
}

export interface DropResult {
    moveToId: string,
    addLast: boolean
}

export default function ShoppingListGrid({ items, onDelete, onCardReorder, height }: Props) {
    const [runDeleteAnimation, setRunDeleteAnimation] = useState(false);
    const [idsToDelete, setIdsToDelete] = useState<string[]>([]);
    const [movingCardId, setMovingCardId] = useState<string | null>(null);
    const [movingCardPos, setMovingCardPos] = useState({ x: 0, y: 0 })
    const rectsRef = useRef<Record<string, DOMRect | null>>({});

    const updateRect = (id: string, rect: DOMRect) => {
        rectsRef.current[id] = rect;
    };

    const getHoveredId = (): DropResult | null => {
        const { x, y } = movingCardPos;
        let hovered: string | null = null;
        const lastId = items[items.length - 1].id;
        for (const [id, rect] of Object.entries(rectsRef.current)) {
            if (
                rect &&
                x > rect.left &&
                x < rect.right &&
                y > rect.top &&
                y < rect.bottom &&
                id !== movingCardId
            ) {
                hovered = id;
                break;
            }
        }
        if (hovered === null) {
            if (lastId === movingCardId) return null;
            const lastCard = Object.entries(rectsRef.current).find(r => r[0] === lastId);
            if (!lastCard || lastCard.length < 2 || lastCard[1] === null) return null;
            if (y > lastCard[1].top) return { moveToId: lastId, addLast: true };
        }
        if (hovered === null) return null;
        return { moveToId: hovered, addLast: false };
    }

    const handleCardRelease = (movedCardId: string) => {
        const hoverId = getHoveredId();
        if (hoverId) {
            onCardReorder(movedCardId, hoverId);
        }
    }

    useEffect(() => {
        if (idsToDelete.length > 0) {
            const timer = setTimeout(() => {
                setRunDeleteAnimation(false);
                const id = idsToDelete.shift();
                if (!id) return;
                onDelete(id);
                // const timer2 = setTimeout(() => {
                //     setIdsToDelete('');
                // }, 600)
                // return () => clearTimeout(timer2);

            }, 300); // Match this to your animation duration (0.5s = 500ms)

            return () => clearTimeout(timer);
        }
    }, [idsToDelete]);

    const handleDelete = (id: string) => {
        setIdsToDelete([...idsToDelete, id]);
        // setRunDeleteAnimation(true);
    }

    return (
        <div className="grid" style={{ maxHeight: height }}>
            {items.map((item) => (
                <CardItem
                    key={item.id}
                    item={item}
                    isDeleting={idsToDelete.includes(item.id)}
                    onDelete={handleDelete}
                    movingCardId={movingCardId}
                    setMovingCardId={setMovingCardId}
                    pos={movingCardPos}
                    setPos={(x: number, y: number) => setMovingCardPos({ x, y })}
                    onRelease={handleCardRelease}
                    reportRect={updateRect}
                    items={items}
                />
            ))}
        </div>
    )
}

interface CardProps {
    item: ShoppingListItem;
    isDeleting: boolean;
    onDelete: (id: string) => void;
    movingCardId: string | null;
    setMovingCardId: (id: string | null) => void;
    pos: { x: number, y: number }
    setPos: (x: number, y: number) => void;
    onRelease: (cardId: string) => void;
    reportRect: (id: string, rect: DOMRect) => void;
    items: ShoppingListItem[];

}

function CardItem({ item, isDeleting, onDelete, movingCardId, setMovingCardId, pos, setPos, onRelease, reportRect, items }: CardProps) {
    const [show, setShow] = useState(true)
    const [dropArea, setDropArea] = useState(false)
    const isMoving = movingCardId === item.id;
    const ref = useRef<HTMLDivElement>(null);


    useEffect(() => {
        if (!ref.current) return;

        const measure = () => {
            if (!ref.current || !ref.current.isConnected) return;
            const rect = ref.current.getBoundingClientRect();
            reportRect(item.id, rect);
        };

        // measure immediately
        measure();

        // observe size/position changes
        const observer = new ResizeObserver(measure);
        observer.observe(ref.current);

        return () => {
            observer.disconnect();
        };
    }, [item.id, reportRect]);

    useEffect(() => {
        if (item.runPopIn) {
            requestAnimationFrame(() => {
                setShow(true);
            });
        }
    }, [item.runPopIn]);


    useEffect(() => {
        const handleMove = (e: MouseEvent | TouchEvent) => {
            if (!isMoving) return;
            if (e.cancelable) e.preventDefault(); // stops scrolling
            let x = 0;
            let y = 0;

            if (e instanceof TouchEvent) {
                x = e.touches[0].clientX;
                y = e.touches[0].clientY;
            } else {
                x = e.clientX;
                y = e.clientY;
            }

            setPos(x, y);

        };


        const handleUp = () => {
            if (movingCardId) {
                onRelease(movingCardId);
            }
            setMovingCardId(null);
        }

        if (isMoving) {
            window.addEventListener("mousemove", handleMove);
            window.addEventListener("touchmove", handleMove, { passive: false });
            window.addEventListener("mouseup", handleUp);
            window.addEventListener("touchend", handleUp);
        }

        return () => {
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("touchmove", handleMove);
            window.removeEventListener("mouseup", handleUp);
            window.removeEventListener("touchend", handleUp);
        };
    }, [isMoving, pos, movingCardId, onRelease]);

    useEffect(() => {
        if (!movingCardId || movingCardId === item.id) {
            setDropArea(false);
            // setHoveredCardId('');
            return;
        }

        if (!ref.current) return;

        const rect = ref.current.getBoundingClientRect();

        const isInside =
            pos.x > rect.left &&
            pos.x < rect.right &&
            pos.y > rect.top &&
            pos.y < rect.bottom;

        setDropArea(isInside);
    }, [pos, movingCardId]);


    const onHold = (e: React.MouseEvent | React.TouchEvent) => {
        // e.preventDefault();
        setMovingCardId(item.id);

        // set initial position
        let x = 0;
        let y = 0;

        if ("touches" in e) {
            x = e.touches[0].clientX;
            y = e.touches[0].clientY;
        } else {
            x = e.clientX;
            y = e.clientY;
        }

        setPos(x, y);
    };

    const createDropArea = () => {
        if (movingCardId !== item.id && movingCardId !== null) {
            setDropArea(true);
        }
    }

    return (
        <div
            onMouseEnter={createDropArea}
            onMouseLeave={() => setDropArea(false)}
            ref={ref}
            style={isMoving ? {
                // minHeight: 3,
                height: 3,
                overflow: 'visible',
                backgroundColor: '#a178a11e',
            } : {}}
        >
            {dropArea && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        padding: '16px',
                        justifyContent: 'flex-start',
                    }}
                >
                    <Typography
                        style={{ color: '#8f6b92d3' }}
                    >
                        {items.find(e => e.id === movingCardId)?.text}
                    </Typography>

                </div>
            )}
            <div
                onAnimationEnd={() => setShow(false)}
                className={`card ${isDeleting ? 'remove' : show ? 'show' : 'static'}`}
                style={
                    isMoving ? {
                        touchAction: "none",
                        position: "fixed",
                        top: pos.y - 15,
                        left: pos.x - 40,
                        gap: 40
                        // transform: "translate(50%, -50%)",
                        // boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        // zIndex: 9999,
                    } : {}
                }
            >
                <div
                    style={{ alignSelf: 'center', flex: 1 }}
                    onMouseDown={onHold}
                    onTouchStart={onHold}
                >
                    <Typography
                        style=
                        {{ 
                            wordWrap: 'break-word',
                            hyphens: 'auto',
                            minWidth: 0,
                            fontSize: 12
                        }}

                        
                    >
                        {item.text}
                    </Typography>

                </div>


                <Button
                    style={{
                        color: '#b483ae',
                        width: '44px',
                        minWidth: '44px',
                        marginLeft: 'auto',
                        marginRight: '0px',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(item.id);
                    }}
                >
                    <RemoveCircleOutline />
                </Button>
            </div>

        </div>

    )
}